import { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import MainLayout from '../../components/layout/MainLayout';
import {
  listContactLists,
  createContactList,
  updateContactList,
  deleteContactList,
  addContactListMember,
  removeContactListMember,
  updateContactListMember,
  searchTenantUsersAdmin,
} from '../../api/backendApi';
import { userLabel } from '../../utils/userLabel';

const SCOPE_TYPES = ['tenant', 'site', 'building', 'custom'];
const VISIBILITY_MODES = [
  { value: 'all_admins', label: 'Admins only' },
  { value: 'roles', label: 'Selected roles' },
  { value: 'users', label: 'Selected users' },
];
const AVAILABLE_ROLES = ['operator', 'responder', 'tenant_admin', 'overseer'];

const EMPTY_FORM = {
  name: '',
  description: '',
  active: true,
  scope: { type: 'tenant' },
  visibility: { mode: 'all_admins', roles: [], userIds: [] },
  calling: { enabled: false, timeoutSeconds: 30 },
};

// ---------------------------------------------------------------------------
// List Form Dialog
// ---------------------------------------------------------------------------
function ListFormDialog({ open, onClose, onSave, initial, tenantId }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name || '',
              description: initial.description || '',
              active: initial.active !== false,
              scope: initial.scope || { type: 'tenant' },
              visibility: initial.visibility || { mode: 'all_admins', roles: [], userIds: [] },
              calling: initial.calling || { enabled: false, timeoutSeconds: 30 },
            }
          : { ...EMPTY_FORM }
      );
      setError('');
    }
  }, [open, initial]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const setScope = (field, value) =>
    setForm((f) => ({ ...f, scope: { ...f.scope, [field]: value } }));
  const setVis = (field, value) =>
    setForm((f) => ({ ...f, visibility: { ...f.visibility, [field]: value } }));
  const setCalling = (field, value) =>
    setForm((f) => ({ ...f, calling: { ...f.calling, [field]: value } }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const visMode = form.visibility?.mode || 'all_admins';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? 'Edit Contact List' : 'Create Contact List'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          fullWidth required sx={{ mb: 2 }}
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          fullWidth multiline rows={2} sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={<Switch checked={form.active} onChange={(e) => set('active', e.target.checked)} />}
          label="Active"
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Scope</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Scope Type</InputLabel>
          <Select
            value={form.scope?.type || 'tenant'}
            label="Scope Type"
            onChange={(e) => setScope('type', e.target.value)}
          >
            {SCOPE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Visibility</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Visible to</InputLabel>
          <Select
            value={visMode}
            label="Visible to"
            onChange={(e) => setVis('mode', e.target.value)}
          >
            {VISIBILITY_MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {visMode === 'roles' && (
          <Autocomplete
            multiple
            options={AVAILABLE_ROLES}
            value={form.visibility?.roles || []}
            onChange={(_, v) => setVis('roles', v)}
            renderInput={(params) => <TextField {...params} label="Roles" />}
            sx={{ mb: 2 }}
          />
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Calling</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!form.calling?.enabled}
              onChange={(e) => setCalling('enabled', e.target.checked)}
            />
          }
          label="Enable Calling (Ring All / First Answer)"
          sx={{ mb: 1 }}
        />
        {form.calling?.enabled && (
          <TextField
            label="Call Timeout (seconds)"
            type="number"
            value={form.calling?.timeoutSeconds ?? 30}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isNaN(v)) setCalling('timeoutSeconds', Math.max(5, Math.min(120, v)));
            }}
            inputProps={{ min: 5, max: 120, step: 5 }}
            helperText="5–120 seconds. All members ring simultaneously; first to answer wins."
            fullWidth
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Member Row
// ---------------------------------------------------------------------------
function MemberRow({ member, onRemove, onToggle, onPriorityChange }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        py: 0.75, px: 1, borderRadius: 1,
        bgcolor: member.active ? 'transparent' : 'action.disabledBackground',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {member.displayName || member.userId}
        </Typography>
        {member.email && (
          <Typography variant="caption" color="text.secondary" noWrap>{member.email}</Typography>
        )}
      </Box>
      <TextField
        type="number"
        size="small"
        label="Priority"
        value={member.priority ?? 0}
        onChange={(e) => onPriorityChange(member.id, Number(e.target.value))}
        sx={{ width: 80 }}
        inputProps={{ min: 0, max: 99 }}
      />
      <Tooltip title={member.active ? 'Deactivate member' : 'Activate member'}>
        <Switch
          size="small"
          checked={!!member.active}
          onChange={() => onToggle(member)}
        />
      </Tooltip>
      <Tooltip title="Remove member">
        <IconButton size="small" color="error" onClick={() => onRemove(member.id)}>
          <PersonRemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Members Panel (expanded inline below each row)
// ---------------------------------------------------------------------------
function MembersPanel({ list, tenantId, onListUpdated }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addError, setAddError] = useState('');
  const [members, setMembers] = useState(list.members || []);

  useEffect(() => { setMembers(list.members || []); }, [list]);

  const handleSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const users = await searchTenantUsersAdmin(tenantId, q, 20);
      const existingUserIds = new Set(members.map((m) => m.userId));
      setSearchResults(users.filter((u) => !existingUserIds.has(u.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [tenantId, members]);

  const handleAdd = async (user) => {
    setAddError('');
    try {
      const member = {
        type: 'user',
        userId: user.id,
        displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        email: user.email,
        priority: 0,
        active: true,
      };
      const resp = await addContactListMember(tenantId, list.id, member);
      const added = resp?.data?.member || member;
      setMembers((prev) => [...prev, added]);
      setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      setSearchQuery('');
      onListUpdated();
    } catch (e) {
      setAddError(e?.message || 'Failed to add member');
    }
  };

  const handleRemove = async (memberId) => {
    try {
      await removeContactListMember(tenantId, list.id, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      onListUpdated();
    } catch (e) {
      setAddError(e?.message || 'Failed to remove member');
    }
  };

  const handleToggle = async (member) => {
    try {
      await updateContactListMember(tenantId, list.id, member.id, { active: !member.active });
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, active: !m.active } : m));
      onListUpdated();
    } catch (e) {
      setAddError(e?.message || 'Failed to update member');
    }
  };

  const handlePriorityChange = async (memberId, priority) => {
    setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, priority } : m));
    try {
      await updateContactListMember(tenantId, list.id, memberId, { priority });
    } catch {
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
      {addError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setAddError('')}>{addError}</Alert>}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Members ({members.length})
      </Typography>

      {members.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No members yet.
        </Typography>
      )}

      {members.map((m) => (
        <MemberRow
          key={m.id}
          member={m}
          onRemove={handleRemove}
          onToggle={handleToggle}
          onPriorityChange={handlePriorityChange}
        />
      ))}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Add member</Typography>
      <Autocomplete
        options={searchResults}
        getOptionLabel={(u) => userLabel(u)}
        filterOptions={(x) => x}
        inputValue={searchQuery}
        onInputChange={(_, v) => {
          setSearchQuery(v);
          handleSearch(v);
        }}
        onChange={(_, user) => { if (user) handleAdd(user); }}
        loading={searching}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search users"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={searchQuery.length < 2 ? 'Type at least 2 characters' : 'No users found'}
        sx={{ maxWidth: 400 }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function ContactListsPage() {
  const { userData, tenantId: authTenantId } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const tenantId = (dealerAdmin.active ? dealerAdmin.tenantId : '') || authTenantId || userData?.tenantId;
  const role = userData?.role;
  const hasAdminAccess = Boolean(dealerAdmin.active) || ['admin', 'superadmin', 'tenant_admin', 'overseer'].includes(role);

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const [expandedId, setExpandedId] = useState(null);

  const canWrite = Boolean(dealerAdmin.active) || ['admin', 'superadmin', 'tenant_admin'].includes(role);

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    try {
      const items = await listContactLists(tenantId, { includeInactive });
      setLists(items);
      setError('');
    } catch (e) {
      setError(e?.message || 'Failed to load contact lists');
    } finally {
      setLoading(false);
    }
  }, [tenantId, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (formData) => {
    if (editTarget) {
      await updateContactList(tenantId, editTarget.id, formData);
    } else {
      await createContactList(tenantId, formData);
    }
    await load();
  };

  const handleDuplicate = async (item) => {
    await createContactList(tenantId, {
      name: `${item.name} (copy)`,
      description: item.description,
      active: false,
      scope: item.scope,
      visibility: item.visibility,
      members: [],
    });
    await load();
  };

  const handleDeactivate = async (item) => {
    await updateContactList(tenantId, item.id, { ...item, active: !item.active });
    await load();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await deleteContactList(tenantId, item.id);
      await load();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  const columns = [
    {
      field: 'expand',
      headerName: '',
      width: 44,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => setExpandedId((prev) => (prev === params.row.id ? null : params.row.id))}
        >
          {expandedId === params.row.id ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 180 },
    {
      field: 'active',
      headerName: 'Status',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.row.active ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.active ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'members',
      headerName: 'Members',
      width: 90,
      valueGetter: (params) => {
        const row = params?.row || params;
        return Array.isArray(row?.members) ? row.members.length : 0;
      },
    },
    {
      field: 'scope',
      headerName: 'Scope',
      width: 100,
      valueGetter: (params) => {
        const row = params?.row || params;
        return row?.scope?.type || '—';
      },
    },
    {
      field: 'visibility',
      headerName: 'Visibility',
      width: 120,
      valueGetter: (params) => {
        const row = params?.row || params;
        return row?.visibility?.mode || '—';
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 130,
      valueGetter: (params) => {
        const row = params?.row || params;
        const ts = row?.updatedAt;
        if (!ts) return '—';
        try { return new Date(ts).toLocaleDateString(); } catch { return ts; }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit">
              <span>
                <IconButton size="small" disabled={!canWrite} onClick={() => { setEditTarget(row); setFormOpen(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Duplicate">
              <span>
                <IconButton size="small" disabled={!canWrite} onClick={() => handleDuplicate(row)}>
                  <DuplicateIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.active ? 'Deactivate' : 'Activate'}>
              <span>
                <IconButton size="small" disabled={!canWrite} onClick={() => handleDeactivate(row)}>
                  <PersonAddIcon fontSize="small" color={row.active ? 'warning' : 'success'} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton size="small" color="error" disabled={!canWrite} onClick={() => handleDelete(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (!hasAdminAccess) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Admin access required.</Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              Contact Lists
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reusable groups of contacts for operational calling, notifications, and future incident bridges.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
              }
              label="Show inactive"
            />
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setEditTarget(null); setFormOpen(true); }}
              >
                New List
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <DataGrid
              rows={lists}
              columns={columns}
              loading={loading}
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              getDetailPanelHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
              }}
            />
          </CardContent>
        </Card>

        {expandedId && (
          (() => {
            const expandedList = lists.find((l) => l.id === expandedId);
            if (!expandedList) return null;
            return (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {expandedList.name} — Members
                  </Typography>
                  {canWrite ? (
                    <MembersPanel
                      list={expandedList}
                      tenantId={tenantId}
                      onListUpdated={load}
                    />
                  ) : (
                    <Box>
                      {(expandedList.members || []).map((m) => (
                        <Typography key={m.id} variant="body2" sx={{ py: 0.5 }}>
                          {m.displayName || m.userId}
                          {m.email ? ` — ${m.email}` : ''}
                          {!m.active ? ' (inactive)' : ''}
                        </Typography>
                      ))}
                      {!expandedList.members?.length && (
                        <Typography variant="body2" color="text.secondary">No members.</Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()
        )}

        {!loading && lists.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No contact lists yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a list to group users for operational calling and notifications.
            </Typography>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setEditTarget(null); setFormOpen(true); }}
              >
                Create Contact List
              </Button>
            )}
          </Box>
        )}
      </Box>

      <ListFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        initial={editTarget}
        tenantId={tenantId}
      />
    </MainLayout>
  );
}
